/**
 * Shared filter matching logic for TableFilters (number, date, text/select).
 * Single source of truth for operator semantics — used by Leads, Projects, Finances.
 */

import type { FilterFieldType } from '../components/ui/TableFilters';

/**
 * Determines if a cell value matches a filter (operator + value).
 * Used by list/table pages to apply active filters consistently.
 *
 * @param fieldType - Column type: 'number' | 'date' | 'text' | 'select'
 * @param operator - Filter operator (is, is_not, contains, greater_than, before, etc.)
 * @param filterValue - Raw value from the filter (string, e.g. "100" or "2025-01-15")
 * @param cellValue - Raw value from the row (string, number, or null/undefined)
 * @returns true if the cell matches the filter condition
 */
export function matchFilterValue(
  fieldType: FilterFieldType,
  operator: string,
  filterValue: string,
  cellValue: string | number | null | undefined
): boolean {
  const v = (filterValue ?? '').trim();
  const vLower = v.toLowerCase();

  if (fieldType === 'number') {
    const num = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue ?? ''));
    const filterNum = parseFloat(v);
    const empty = cellValue == null || Number.isNaN(num);
    if (operator === 'is_empty') return empty;
    if (operator === 'is_not_empty') return !empty;
    if (empty || (v !== '' && Number.isNaN(filterNum))) return false;
    if (operator === 'is') return !Number.isNaN(filterNum) && num === filterNum;
    if (operator === 'is_not') return Number.isNaN(filterNum) || num !== filterNum;
    if (operator === 'greater_than') return !Number.isNaN(filterNum) && num > filterNum;
    if (operator === 'less_than') return !Number.isNaN(filterNum) && num < filterNum;
    return true;
  }

  if (fieldType === 'date') {
    const d = cellValue != null && cellValue !== '' ? new Date(String(cellValue)).getTime() : NaN;
    const filterD = v ? new Date(v).getTime() : NaN;
    const empty = Number.isNaN(d);
    if (operator === 'is_empty') return empty;
    if (operator === 'is_not_empty') return !empty;
    if (empty || !v || Number.isNaN(filterD)) return false;
    const cellDateStr = cellValue != null && cellValue !== '' ? new Date(String(cellValue)).toISOString().slice(0, 10) : '';
    if (operator === 'is') return cellDateStr === v;
    if (operator === 'is_not') return cellDateStr !== v;
    if (operator === 'before') return d < filterD;
    if (operator === 'after') return d > filterD;
    return true;
  }

  // text | select
  const str = (cellValue != null && cellValue !== '' ? String(cellValue) : '').toLowerCase();
  const empty = !str;
  if (operator === 'is_empty') return empty;
  if (operator === 'is_not_empty') return !empty;
  if (empty) return false;
  if (operator === 'is' || operator === 'is_not') {
    const eq = str === vLower;
    return operator === 'is' ? eq : !eq;
  }
  if (operator === 'contains') return str.includes(vLower);
  if (operator === 'not_contains') return !str.includes(vLower);
  return true;
}
